import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import type { FromStopDraftByUid, QuantityDraftByCargo, ToStopDraftByUid } from './executionDraftTypes';
import { getOrderedStops } from './editorStopOrdering';

export type ItinerarySafetyIssue = {
  code:
    | 'UNLOAD_BEFORE_PARENT_LOAD'
    | 'CARGO_UNLOAD_BEFORE_LOAD'
    | 'MISSING_PARENT_LOADING';
  stopUid?: string;
  cargoId?: number;
  message: string;
};

function findStopByDbId(
  groups: DeliveryGroupDraft[],
  stopId: number
): DeliveryGroupStopDraft | null {
  for (const g of groups) {
    for (const s of [...g.loadingStops, ...g.unloadingStops]) {
      if (s.id != null && Number(s.id) === stopId) return s;
    }
  }
  return null;
}

/** Resolve parent loading position for an unloading stop (structural, not execution engine). */
export function resolveParentLoadingPosition(
  groups: DeliveryGroupDraft[],
  unload: DeliveryGroupStopDraft,
  groupUid: string
): number | null {
  if (unload.type !== 'unloading') return null;

  if (unload.parentLoadingStopId != null) {
    const parent = findStopByDbId(groups, Number(unload.parentLoadingStopId));
    if (parent?.position != null && parent.position >= 0) return parent.position;
  }

  if (unload.parentLoadingPosition != null && unload.parentLoadingPosition >= 0) {
    return unload.parentLoadingPosition;
  }

  const group = groups.find((g) => g.uid === groupUid);
  if (!group) return null;

  const loadingPositions = group.loadingStops
    .map((s) => s.position)
    .filter((p): p is number => p != null && p >= 0);
  if (loadingPositions.length > 0) {
    return Math.min(...loadingPositions);
  }

  return group.isDefault ? 0 : group.loadingPosition ?? null;
}

/**
 * Structural guard: each unloading must appear after its parent loading in itinerary order.
 */
export function validateItineraryAllocationSafety(
  groups: DeliveryGroupDraft[]
): ItinerarySafetyIssue[] {
  const issues: ItinerarySafetyIssue[] = [];
  const ordered = getOrderedStops(groups);

  for (const entry of ordered) {
    if (entry.stop.type !== 'unloading') continue;
    const unloadPos = Number(entry.stop.position ?? -1);
    if (unloadPos < 0) continue;

    const parentPos = resolveParentLoadingPosition(groups, entry.stop, entry.groupUid);
    if (parentPos == null) {
      issues.push({
        code: 'MISSING_PARENT_LOADING',
        stopUid: entry.stop.uid,
        message: `Точка выгрузки «${entry.stop.address || entry.stop.uid}» не привязана к погрузке`
      });
      continue;
    }

    if (unloadPos <= parentPos) {
      issues.push({
        code: 'UNLOAD_BEFORE_PARENT_LOAD',
        stopUid: entry.stop.uid,
        message:
          `Недопустимый порядок: выгрузка (позиция ${unloadPos + 1}) не может быть раньше ` +
          `своей погрузки (позиция ${parentPos + 1}). Переместите выгрузку ниже погрузки.`
      });
    }
  }

  return issues;
}

/** Cargo bindings must respect itinerary: loading stop before unloading stop. */
export function validateCargoItineraryOrder(params: {
  groups: DeliveryGroupDraft[];
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
}): ItinerarySafetyIssue[] {
  const issues: ItinerarySafetyIssue[] = [];
  const positionByUid = new Map<string, number>();
  for (const entry of getOrderedStops(params.groups)) {
    if (entry.stop.position != null && entry.stop.position >= 0) {
      positionByUid.set(entry.stop.uid, entry.stop.position);
    }
  }

  for (const cargoId of params.selectedIds) {
    const qty = Number(params.quantityDraft[cargoId] ?? 0);
    if (qty <= 0) continue;

    const fromUid = params.fromStopDraft[cargoId];
    const toUid = params.toStopDraft[cargoId];
    if (!fromUid || !toUid) continue;

    const fromPos = positionByUid.get(fromUid);
    const toPos = positionByUid.get(toUid);
    if (fromPos == null || toPos == null) continue;

    if (toPos <= fromPos) {
      issues.push({
        code: 'CARGO_UNLOAD_BEFORE_LOAD',
        cargoId,
        message:
          `Груз #${cargoId}: точка выгрузки (позиция ${toPos + 1}) не может быть раньше ` +
          `точки погрузки (позиция ${fromPos + 1}) в маршруте`
      });
    }
  }

  return issues;
}

export function validateItineraryBeforeSave(params: {
  groups: DeliveryGroupDraft[];
  selectedIds?: number[];
  fromStopDraft?: FromStopDraftByUid;
  toStopDraft?: ToStopDraftByUid;
  quantityDraft?: QuantityDraftByCargo;
}): ItinerarySafetyIssue[] {
  const structural = validateItineraryAllocationSafety(params.groups);
  if (structural.length > 0) return structural;

  if (
    params.selectedIds?.length &&
    params.fromStopDraft &&
    params.toStopDraft &&
    params.quantityDraft
  ) {
    return validateCargoItineraryOrder({
      groups: params.groups,
      selectedIds: params.selectedIds,
      fromStopDraft: params.fromStopDraft,
      toStopDraft: params.toStopDraft,
      quantityDraft: params.quantityDraft
    });
  }

  return [];
}
